import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle, Archive, CalendarClock, FileDown, PlayCircle, StopCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayBR = () => {
  const n = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const ResetTemporada = () => {
  const editAllowed = canEdit();
  const [currentSeason, setCurrentSeason] = useState({ season_start: '', season_end: null, season_name: '', is_closed: false });
  const [historyList, setHistoryList] = useState([]);
  const [openStep, setOpenStep] = useState(null); // null | 'edit' | 'close' | 'closeConfirm' | 'start' | 'startConfirm'
  const [confirmationText, setConfirmationText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEnd, setEditEnd] = useState('');
  const [closeEndDate, setCloseEndDate] = useState('');
  const [closeSeasonName, setCloseSeasonName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startEndDate, setStartEndDate] = useState('');
  const [startName, setStartName] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [cur, hist] = await Promise.all([
        axios.get(`${API}/scoring/current-season`, { headers: getAdminHeaders() }),
        axios.get(`${API}/scoring/seasons-history`, { headers: getAdminHeaders() }),
      ]);
      setCurrentSeason({
        season_start: cur.data?.season_start || '',
        season_end: cur.data?.season_end || null,
        season_name: cur.data?.season_name || '',
        is_closed: !!cur.data?.is_closed,
      });
      setHistoryList(hist.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const openEditModal = () => {
    if (!editAllowed) return;
    setEditName(currentSeason.season_name || '');
    setEditEnd(currentSeason.season_end || '');
    setOpenStep('edit');
  };

  const saveEdit = async () => {
    try {
      await axios.patch(
        `${API}/scoring/current-season`,
        { season_name: editName, season_end: editEnd || null },
        { headers: getAdminHeaders() }
      );
      toast.success('Ciclo atualizado');
      setOpenStep(null);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao atualizar ciclo');
    }
  };

  const openCloseModal = () => {
    if (!editAllowed) return;
    setCloseEndDate(currentSeason.season_end || todayBR());
    setCloseSeasonName(currentSeason.season_name || `Temporada ${currentSeason.season_start} a ${currentSeason.season_end || todayBR()}`);
    setConfirmationText('');
    setOpenStep('close');
  };

  const goToCloseConfirm = () => {
    if (!closeEndDate) {
      toast.error('Escolha a data de encerramento');
      return;
    }
    if (closeEndDate < currentSeason.season_start) {
      toast.error(`Data de encerramento deve ser >= inicio (${currentSeason.season_start})`);
      return;
    }
    if (closeEndDate > todayBR()) {
      toast.error('Data de encerramento nao pode ser futura');
      return;
    }
    setOpenStep('closeConfirm');
  };

  const doClose = async () => {
    if ((confirmationText || '').trim().toUpperCase() !== 'CONFIRMAR') {
      toast.error('Digite CONFIRMAR para prosseguir');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/scoring/close-season`,
        { end_date: closeEndDate, confirmation: 'CONFIRMAR', season_name: closeSeasonName },
        { headers: getAdminHeaders() }
      );
      toast.success(`Ciclo encerrado em ${closeEndDate}. Lancamentos bloqueados ate o proximo ciclo.`);
      setOpenStep(null);
      setConfirmationText('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao encerrar ciclo');
    } finally {
      setSubmitting(false);
    }
  };

  const openStartModal = () => {
    if (!editAllowed) return;
    const last = historyList[0];
    setStartDate(last?.end_date || todayBR());
    setStartEndDate('');
    setStartName('');
    setConfirmationText('');
    setOpenStep('start');
  };

  const goToStartConfirm = () => {
    if (!startDate) {
      toast.error('Escolha a data de inicio');
      return;
    }
    if (startEndDate && startEndDate < startDate) {
      toast.error('Data de fim deve ser >= inicio');
      return;
    }
    setOpenStep('startConfirm');
  };

  const doStart = async () => {
    if ((confirmationText || '').trim().toUpperCase() !== 'CONFIRMAR') {
      toast.error('Digite CONFIRMAR para prosseguir');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/scoring/start-season`,
        {
          start_date: startDate,
          end_date: startEndDate || null,
          season_name: startName,
          confirmation: 'CONFIRMAR',
        },
        { headers: getAdminHeaders() }
      );
      toast.success(`Novo ciclo iniciado em ${startDate}!`);
      setOpenStep(null);
      setConfirmationText('');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao iniciar ciclo');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async (season) => {
    try {
      const res = await axios.get(
        `${API}/scoring/seasons-history/${season.id}/pdf`,
        { headers: getAdminHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(season.name || 'temporada').replace(/[^\w\-]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF baixado');
    } catch (e) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const isActive = !currentSeason.is_closed;

  return (
    <Card data-testid="reset-season-card" className={`bg-white rounded-2xl p-6 shadow-md ${isActive ? 'border border-emerald-200/60' : 'border-2 border-red-300'} mt-6`}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`rounded-full p-2 flex-shrink-0 ${isActive ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {isActive ? <PlayCircle className="w-5 h-5 text-emerald-700" /> : <StopCircle className="w-5 h-5 text-red-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-nunito font-bold text-lg text-[#1A4D2E]">Ciclo do Projeto Sementes</h3>
            <span
              data-testid="season-status-badge"
              className={`text-xs font-bold px-2 py-0.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`}
            >
              {isActive ? 'ATIVO' : 'ENCERRADO'}
            </span>
          </div>
          <p className="font-inter text-sm text-gray-600 mt-1">
            {isActive
              ? 'Todos os lancamentos de sementes contam para o ciclo em curso.'
              : 'Sem ciclo ativo — lancamentos de sementes estao BLOQUEADOS. Inicie um novo ciclo para retomar.'}
          </p>

          {currentSeason.season_start && (
            <div className={`mt-3 rounded-xl p-3 border ${isActive ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-nunito font-bold text-sm text-[#1A4D2E]" data-testid="current-season-name">
                    {currentSeason.season_name || '(sem nome definido)'}
                  </p>
                  <p className="font-inter text-xs text-gray-600 mt-1 flex items-center gap-1 flex-wrap">
                    <CalendarClock className="w-3.5 h-3.5" />
                    Inicio: <strong>{currentSeason.season_start}</strong>
                    {currentSeason.season_end && (
                      <>
                        <span className="mx-1">·</span>
                        Fim: <strong>{currentSeason.season_end}</strong>
                      </>
                    )}
                    {!currentSeason.season_end && (
                      <span className="ml-1 text-gray-400">(sem data de fim definida)</span>
                    )}
                  </p>
                </div>
                {isActive && editAllowed && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="btn-edit-current-season"
                    onClick={openEditModal}
                    className="border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#1A4D2E]/5 flex-shrink-0"
                  >
                    Editar / Estender
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {isActive ? (
          <Button
            type="button"
            variant="outline"
            onClick={openCloseModal}
            disabled={!editAllowed}
            data-testid="btn-open-close"
            className="border-red-300 text-red-600 hover:bg-red-50 rounded-full"
          >
            <StopCircle className="w-4 h-4 mr-2" /> Encerrar ciclo
          </Button>
        ) : (
          <Button
            type="button"
            onClick={openStartModal}
            disabled={!editAllowed}
            data-testid="btn-open-start"
            className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full"
          >
            <PlayCircle className="w-4 h-4 mr-2" /> Iniciar novo ciclo
          </Button>
        )}
      </div>

      {historyList.length > 0 && (
        <div className="mt-6 border-t border-gray-100 pt-4">
          <h4 className="font-nunito font-bold text-sm text-[#1A4D2E] flex items-center gap-2 mb-3">
            <Archive className="w-4 h-4" /> Temporadas arquivadas ({historyList.length})
          </h4>
          <div className="space-y-2" data-testid="seasons-history-list">
            {historyList.map((s) => (
              <div
                key={s.id}
                data-testid={`season-history-${s.id}`}
                className="flex items-start justify-between gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-nunito font-bold text-sm text-[#1A4D2E]">{s.name}</p>
                  <p className="text-xs text-gray-600">
                    Periodo: <span className="font-semibold">{s.start_date}</span> a <span className="font-semibold">{s.end_date}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    {s.total_children} criancas · reset por {s.reset_by_name || s.reset_by_email}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid={`btn-download-pdf-${s.id}`}
                  onClick={() => downloadPdf(s)}
                  className="border-[#1A4D2E]/30 text-[#1A4D2E] hover:bg-[#1A4D2E]/5 flex-shrink-0"
                >
                  <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit current season */}
      <Dialog open={openStep === 'edit'} onOpenChange={(o) => { if (!o) setOpenStep(null); }}>
        <DialogContent data-testid="edit-season-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1A4D2E]">Editar / Estender ciclo atual</DialogTitle>
            <DialogDescription>Altere o nome ou estenda a data de fim previsto.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-name">Nome</Label>
              <Input id="edit-name" data-testid="input-edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Ex: Projeto Sementes 1o Semestre 2026" maxLength={120} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label htmlFor="edit-end">Data de fim</Label>
              <Input id="edit-end" type="date" data-testid="input-edit-end" value={editEnd} min={currentSeason.season_start} onChange={(e) => setEditEnd(e.target.value)} className="rounded-xl mt-1" />
              <p className="text-xs text-gray-500 mt-1">Deixe em branco para ciclo em aberto.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenStep(null)}>Cancelar</Button>
            <Button data-testid="btn-save-edit-season" onClick={saveEdit} className="bg-[#1A4D2E] hover:bg-[#145A32] text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close season step 1 */}
      <Dialog open={openStep === 'close'} onOpenChange={(o) => { if (!o) setOpenStep(null); }}>
        <DialogContent data-testid="close-season-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><StopCircle className="w-5 h-5" /> Encerrar ciclo</DialogTitle>
            <DialogDescription>
              Congela o ranking na data informada. A data pode ser <strong>retroativa</strong>.
              Apos encerrar, lancamentos ficam bloqueados ate um novo ciclo comecar.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="close-name">Nome do arquivo (temporada encerrada)</Label>
              <Input id="close-name" data-testid="input-close-name" value={closeSeasonName} onChange={(e) => setCloseSeasonName(e.target.value)} maxLength={120} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label htmlFor="close-end">Data de encerramento</Label>
              <Input
                id="close-end" type="date" data-testid="input-close-end"
                value={closeEndDate}
                min={currentSeason.season_start}
                max={todayBR()}
                onChange={(e) => setCloseEndDate(e.target.value)}
                className="rounded-xl mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Pode ser retroativa. Deve estar entre {currentSeason.season_start} e hoje.
              </p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-900">
                Ranking e Portal da Familia serao congelados nessa data. Lancamentos apos essa data ficam bloqueados ate voce iniciar um novo ciclo.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenStep(null)}>Cancelar</Button>
            <Button onClick={goToCloseConfirm} data-testid="btn-next-close-confirm" className="bg-red-600 hover:bg-red-700 text-white">Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close season step 2 */}
      <Dialog open={openStep === 'closeConfirm'} onOpenChange={(o) => { if (!o) setOpenStep(null); }}>
        <DialogContent data-testid="close-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Confirmacao final
            </DialogTitle>
            <DialogDescription>
              Encerrar <strong>&quot;{closeSeasonName}&quot;</strong> em <strong>{closeEndDate}</strong>.
              Sem novo ciclo apos isso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="close-confirm">Digite <span className="font-bold text-red-600">CONFIRMAR</span></Label>
            <Input id="close-confirm" data-testid="input-close-confirm" value={confirmationText} onChange={(e) => setConfirmationText(e.target.value)} placeholder="CONFIRMAR" className="rounded-xl uppercase" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenStep('close')}>Voltar</Button>
            <Button
              onClick={doClose}
              disabled={submitting || confirmationText.trim().toUpperCase() !== 'CONFIRMAR'}
              data-testid="btn-confirm-close"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? 'Encerrando...' : 'Sim, encerrar ciclo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start season step 1 */}
      <Dialog open={openStep === 'start'} onOpenChange={(o) => { if (!o) setOpenStep(null); }}>
        <DialogContent data-testid="start-season-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]"><PlayCircle className="w-5 h-5" /> Iniciar novo ciclo</DialogTitle>
            <DialogDescription>Defina nome, inicio e (opcional) data-fim previsto. A data-fim pode ser estendida depois.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="start-name">Nome do ciclo</Label>
              <Input id="start-name" data-testid="input-start-name" value={startName} onChange={(e) => setStartName(e.target.value)} placeholder="Ex: Projeto Sementes 2o Semestre 2026" maxLength={120} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label htmlFor="start-date">Data de inicio</Label>
              <Input id="start-date" type="date" data-testid="input-start-date" value={startDate} max={todayBR()} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl mt-1" />
              <p className="text-xs text-gray-500 mt-1">Nao pode ser futura. Deve ser posterior ao fim da temporada anterior (se houver).</p>
            </div>
            <div>
              <Label htmlFor="start-end">Data de fim previsto (opcional)</Label>
              <Input id="start-end" type="date" data-testid="input-start-end" value={startEndDate} min={startDate} onChange={(e) => setStartEndDate(e.target.value)} className="rounded-xl mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenStep(null)}>Cancelar</Button>
            <Button onClick={goToStartConfirm} data-testid="btn-next-start-confirm" className="bg-[#1A4D2E] hover:bg-[#145A32] text-white">Continuar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start season step 2 */}
      <Dialog open={openStep === 'startConfirm'} onOpenChange={(o) => { if (!o) setOpenStep(null); }}>
        <DialogContent data-testid="start-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D2E]"><PlayCircle className="w-5 h-5" /> Confirmar novo ciclo</DialogTitle>
            <DialogDescription>Iniciando <strong>&quot;{startName || '(sem nome)'}&quot;</strong> em <strong>{startDate}</strong>{startEndDate ? <> com fim previsto em <strong>{startEndDate}</strong></> : ''}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="start-confirm">Digite <span className="font-bold text-[#1A4D2E]">CONFIRMAR</span></Label>
            <Input id="start-confirm" data-testid="input-start-confirm" value={confirmationText} onChange={(e) => setConfirmationText(e.target.value)} placeholder="CONFIRMAR" className="rounded-xl uppercase" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenStep('start')}>Voltar</Button>
            <Button
              onClick={doStart}
              disabled={submitting || confirmationText.trim().toUpperCase() !== 'CONFIRMAR'}
              data-testid="btn-confirm-start"
              className="bg-[#1A4D2E] hover:bg-[#145A32] text-white"
            >
              {submitting ? 'Iniciando...' : 'Sim, iniciar novo ciclo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ResetTemporada;
